import { css, html, LitElement, PropertyValues, TemplateResult } from "lit";
import {property, customElement} from 'lit/decorators.js';
import { repeat } from 'lit-html/directives/repeat';
import { FiSubView, UiStateModel, UiStateModelParam } from "../../../src/models/ui-state-model";
import { VoyageShoreContextModel } from "../../../src/models/voyage-shore-context-model";
import { VesselsTrackingModel } from "../../../src/models/vessels-tracking-model";
import { NearestPortEvents, NearestPortModel, NearestPortPosition } from "../../../src/models/nearest-port-model";

import "../../voyage-button/src/voyage-button";
import "../../voy-input-range/src/voy-input-range";
import "../../voy-input-type-a-head/src/voy-input-type-a-head";
import "../../voy-timeinput/src/voy-timeinput";
import "../../voy-dot-menu/src/voy-dot-menu";
import "../../voyage-tooltip/src/voyage-tooltip-proxy";
import { SvgIcons } from "../../voyage-svg-icons/src/voyage-svg-icons";
import { debounce, FormatDateToLocalTime, ScrollTo } from "../../../src/utils/helpers";

import { getDistance as gcDistance } from 'ol/sphere';
import { VoyInputTypeAHead } from "../../voy-input-type-a-head/src/voy-input-type-a-head";
import { CoordinatesUtils } from "../../../src/utils/coordinates-utils";
import { VoyTimeinput } from "../../voy-timeinput/src/voy-timeinput";
import { MapView } from "../../voyage-map/src/voyage-map";
import { VoyModalManager } from "../../voy-modal/src/voy-modal-manager";
import { VoyMapModel } from "../../../src/models/voy-map-model";
import { TerminalsModel } from "../../../src/models/terminals-model";
import { TerminalInfo, TimestampAndTerminal } from "../../../src/models/types/terminals-model.types";
import { IMOsCurrentScheduleState } from "../../../src/models/types/voyage-schedules-model.types";
import { VoyageSchedulesModel } from "../../../src/models/voyage-schedules-model";
import { VesselInfo } from "../../../src/models/types/vessels-model.types";
import { VesselPositionData } from "../../../src/services/types/position-service.types";
import { VesselsModel } from "../../../src/models/vessels-model";
import { VesselVoyageVersion } from "../../voy-global-filters/src/types";

interface PortInfo {
    value: string;
    label: string;
    longitude?: number;
    latitude?: number;
    dist: number;
    time: number; // etaUTC in milliseconds
    etaUTC?: string;
    etaLT?: string;
    terminals: TerminalInfo[];
    coords?: number[][];
};

// Local custom element to define rendering of search results in type ahead search
@customElement('voy-fleet-sub-view-nearest-port-search-result')
export class VoyFleetSubViewNearestPortSearchResult extends LitElement {
    @property({ type: String }) public left?: string;
    @property({ type: String }) public right?: string;

    static styles = css`
        :host {
            font-family: MaerskText;
            font-size: 14px;
            line-height: 16px;
            font-weight: 400;
            display: flex;
            gap: 1em;
        }

        .left {
            flex: 1 1;  
        }

        .right {
            align-self: center;
        }
    `;

    render() {
        return html`
            <div class="left">
                ${this.left ? this.left : ''}
            </div>
            <div class="right">
                ${this.right ? this.right : ''}
            </div>
        `;
    }
}


@customElement('voy-fleet-sub-view-nearest-port')
export class VoyFleetSubViewNearestPort extends LitElement {
    @property({ type: Number }) public imo?: number;
    @property({ attribute: false }) private _vessel?: VesselInfo;
    @property({ attribute: false }) private _speedValue?: number;
    @property({ attribute: false }) private _dropPinActive: boolean = false;
    @property({ attribute: false }) private _droppedPin: PortInfo|undefined = {
        value: 'Click for custom position',
        label: 'Click for custom position',
        dist: -1,
        time: -1,
        terminals: [],
    };
;
    @property({ attribute: false }) private _selection: PortInfo|undefined;
    @property({ attribute: false }) private _schedule: IMOsCurrentScheduleState|undefined;

    @property({ attribute: false }) private _latestPosition: VesselPositionData|undefined;
    @property({ attribute: false }) private _nearestPorts: PortInfo[] = [];
    @property({ attribute: false }) private _manualAdditions: PortInfo[] = [];
    @property({ attribute: false }) private _etd: number|undefined;

    private _searchField: VoyInputTypeAHead|null|undefined;
    // TECHDEBT getting rid of this local copy of ports with an averaged position
    @property({ attribute: false }) private _nearestPortList: PortInfo[] = [];
    @property({ attribute: false }) private _nearestPortSearchResults: PortInfo[] = [];
    private _debouncedConvertToLocaltime: (input: PortInfo[][]) => void;

    constructor() {
        super();

        this._handleRangeChange = this._handleRangeChange.bind(this);
        this._handleKnownTerminalsUpdate = this._handleKnownTerminalsUpdate.bind(this);
        this._handlePositionUpdate = this._handlePositionUpdate.bind(this);
        this._convertToLocaltime = this._convertToLocaltime.bind(this);
        this._handleRulerUpdate = this._handleRulerUpdate.bind(this);
        this._handleETDChange = this._handleETDChange.bind(this);
        this._debouncedConvertToLocaltime = debounce(this._convertToLocaltime, 500);
        this._launchPortMemo = this._launchPortMemo.bind(this);
        this._goBack = this._goBack.bind(this);
    }

    static styles = css`
        :host {
            font-family: MaerskText;
            font-size: 12px;
            line-height: 16px;
            font-weight: 400;
            padding-top: 24px;
            padding-bottom: 0px;
            max-height: calc(100vh - 187px);
            display: flex;
            flex-direction: column;
        }
        section {
            display: flex;
            flex-direction: column;
            padding: 0px 16px 24px 16px;
        }
        .scrollhost + section {
            padding-top: 24px;
        }
        h1 {
            font-family: MaerskHeadline;
            font-size: 26px;
            line-height: 32px;
            margin: 0;
            font-weight: 400;
            color: var(--neutrals-grey-02);
        }
        header {
            display: flex;
            flex-direction: row;
            column-gap: 4px;
            align-items: flex-start;
        }
        header button {
            background: none;
            border: none;
            cursor: pointer;
            padding: 0;
            padding-top: 6px;
            display: flex;
            margin-left: -6px;
        }
        .port {
            padding: 16px;
            border-top: 2px solid var(--neutrals-grey-07, grey);
            border-bottom: 2px solid var(--neutrals-grey-07, grey);
        }
        .port[selected] {
            background: var(--neutrals-grey-08, grey);
        }
        .port~.port {
            border-top: none;
        }
        .port header {
            display: flex;
            flex-direction: row;
            margin-bottom: 8px;
            justify-content: space-between;
        }
        .port header h2 {
            font-size: 14px;
            line-height: 20px;
            font-weight: 400;
            margin: 0;
            color: var(--voyage-blue-dark)
        }
        .port p {
            font-size: 14px;
            line-height: 20px;
            font-weight: 400;
            margin: 0;
            color: var(--neutrals-grey-01)
        }
        .port > p > b {
            margin-left: 0.5em;
            margin-right: 0.5em;
        }
        .port span svg {
            width: 15px;
            height: 15px;
            margin-left: 8px;
            margin-bottom: -3px;
        }
        .eta {
            font-weight: bolder;
        }
        .calculator {
            box-shadow: var(--voyage-box-shadow) 0px 6px 6px;
        }
        .calculator voy-input-range {
            display: block;
            margin-top: 16px;
        }
        .calculator label {
            font-size: 14px;
            line-height: 20px;
            margin-right: 8px;
            color: var(--neutrals-grey-02)
        }
        .calculator .speed-input {
            display: inline-block;
            border: 1px solid var(--neutrals-grey-06);
            padding: 10px 10px;
            border-radius: 5px;
        }
        .calculator .speed-input input {
            padding: 0;
            border: 0;
            font-size: 14px;
            text-align: right;
            width: 30px;
            color: var(--neutrals-grey-02)
        }
        .calculator .speed-input input:active,
        .calculator .speed-input input:focus {
            outline: none;
        }
        .calculator .speed-input input+span {
            font-size: 14px;
            line-height: 20px;
            color: var(--neutrals-grey-05)
        }
        .search {
            padding-top: 24px;
        }
        .search voyage-button {
            margin-top: 8px;
        }
        .scrollhost {
            overflow-x: hidden;
            overflow-y: auto;
        }
        .distance-and-dot-menu-container {
            display: flex;
            align-items: center;
            justify-content: center;
        }
        .dot-menu svg {
            height: 10px;
            width: 10px;
        }
        .distance-tooltip {
            width: 20px;
            height: 20px;
            display: flex;
            flex-direction: row;
            align-self: center;
            margin: 0 25px 0 0;
        }
       .distance .distance-wrapper .distance-tooltip svg {
        width: 100%;
            height: 100%;
            column-gap: 4px;
            fill: var(--primary-blue-900, black);
            margin-left: 0px;
        }
        .distance-wrapper {
            display: flex;
            align-items: center;
            justify-content: center;
        }
        .distance-text {
            margin-right: 8px;
        }
        .premium-icon svg{
            height: 16px;
            width: 16px;
        }
    `;

    connectedCallback() {
        super.connectedCallback();

        TerminalsModel?.addEventListener('terminals-updated', this._handleKnownTerminalsUpdate);
        VesselsTrackingModel?.addEventListener('tracking-updated', this._handlePositionUpdate);
        NearestPortModel!.addEventListener(NearestPortEvents.Ruler, this._handleRulerUpdate);
    }

    disconnectedCallback() {
        super.disconnectedCallback();

        TerminalsModel?.removeEventListener('terminals-updated', this._handleKnownTerminalsUpdate);
        VesselsTrackingModel?.removeEventListener('tracking-updated', this._handlePositionUpdate);
        NearestPortModel!.removeEventListener(NearestPortEvents.Ruler, this._handleRulerUpdate);
        this._selection = undefined;
        this._latestPosition = undefined;
        this._manualAdditions = [];
        this._nearestPorts = [];
        for (let port of this._nearestPortList) {
            port.coords = undefined;
        }
        NearestPortModel?.setDropPin({});
        NearestPortModel?.setPortHighLight({ports: []});
    }

    updated(changedProperties: PropertyValues) {
        super.updated(changedProperties);

        if (changedProperties.has('imo')) {
            this._setVessel()
        }
        if (changedProperties.has('_vessel')) {
            this._handlePositionUpdate();
        }
        if (changedProperties.has('_latestPosition')) {
            if (this._latestPosition) {
                if (!this._speedValue) {
                    this._speedValue = this._latestPosition.sog || 12;
                    this._speedValue = Math.round(this._speedValue*10)/10.0;
                }
            }
            this._updateNearestPorts();
        }
        if (changedProperties.has('_speedValue')) {
            this._handleSpeedChange();
        }
        if (changedProperties.has('_selection')) {
            const selectedElement = this.shadowRoot!.querySelector("[selected]") as HTMLElement;
            if (selectedElement) {
                const scrollHost = this.shadowRoot!.querySelector(".scrollhost") as HTMLElement;
                ScrollTo(scrollHost!, (selectedElement.offsetTop - scrollHost!.offsetTop) - 20, 500);
            }

            this._updateRuler();
            if (this._latestPosition && this._latestPosition.longitude && this._latestPosition.latitude
                && this._selection && this._selection.longitude && this._selection.latitude) {
                const coords = [
                    [this._latestPosition.longitude, this._latestPosition.latitude],
                    [this._selection.longitude, this._selection.latitude],
                    ...(this._selection.coords ? this._selection.coords : [])
                ];

                this.dispatchEvent(new CustomEvent(MapView.AdaptView, {
                    detail: coords,
                    bubbles: true,
                    composed: true,
                }));
            }
        }
        if (changedProperties.has('_nearestPorts')) {
            this._updatePortLayerHighlight();
            this._handleSpeedChange();
            this._updateSearchResults();
        }
        if (changedProperties.has('_manualAdditions')) {
            this._updatePortLayerHighlight();
            this._handleSpeedChange();
            this._updateSearchResults();
        }
        if (changedProperties.has('_nearestPortList')) {
            this._updateNearestPorts();
            this._updateSearchResults();
        }
        if (changedProperties.has('_etd')) {
            this._handleSpeedChange();
        }
    }

    firstUpdated() {
        this._searchField = this.shadowRoot?.querySelector("voy-input-type-a-head");

        this._handleKnownTerminalsUpdate();
    }

    private _updateSearchResults() {
        if (!this._nearestPorts.length) return;
        this._nearestPortSearchResults = this._nearestPortList.filter(port => {
            return !(this._nearestPorts.find(el => el.value === port.value) 
                || this._manualAdditions.find(el => el.value === port.value)
            );
        });
    }

    private async _setVessel(){
        if (!this.imo) return;
        this._vessel = VoyageShoreContextModel!.vessels.find(v => v.imoNo === this.imo);
        if(!this._vessel){
            await VesselsModel?.refreshVessels()
            this._vessel = VesselsModel!.getVessel(this.imo)
        } 
        this._schedule = VoyageSchedulesModel?.currentScheduleStates?.find(s => s.imoNo === this.imo)
    }

    private async _updateNearestPorts(): Promise<void> {
        if (!this._latestPosition) return;

        if (this._nearestPorts.reduce((p, c) => p || !!c.coords, false)) {
            // user has modified one of the routes so do not update the list
            return;
        }

        const {longitude, latitude} = this._latestPosition;

        if (longitude && latitude) {
            const vesselPos = [longitude, latitude];
            this._nearestPortList.forEach((p) => {
                p.dist = gcDistance(vesselPos, [p.longitude, p.latitude]) / 1852;
                p.label = `${p.terminals[0].city} (${p.value})`;
            });
            this._nearestPortList.sort((a, b) => {
                return a.dist - b.dist;
            });
            this._nearestPorts = this._nearestPortList.slice(0, 3);

            this._selection = this._nearestPorts[0];
        }
    }

    private _updatePortLayerHighlight() {
        const ports: NearestPortPosition[] = [
            ...this._nearestPorts.map((p) => ({name: p.value})),
            ...this._manualAdditions.map((p) => ({name: p.value}))
        ];
        NearestPortModel?.setPortHighLight({ports});
    }

    private _updateRuler() {
        const port = this._selection;
        if (!port) return;

        if (port.coords) {
            NearestPortModel?.setDropPin({position: port.coords});
        } else if (this._latestPosition && this._latestPosition.longitude && this._latestPosition.latitude) {
            if (port.longitude && port.latitude) {
                NearestPortModel?.setDropPin({
                    position: [
                        [this._latestPosition?.longitude, this._latestPosition?.latitude],
                        [port.longitude, port.latitude]
                    ]
                });
            } else {
                NearestPortModel?.setDropPin({
                    position: [this._latestPosition?.longitude, this._latestPosition?.latitude],
                });
            }
        }
    }

    private _handleRulerUpdate(evt: Event): void {
        const e = evt as CustomEvent;
        const {distance, coords} = <{distance: number, coords: number[][]}>e.detail;

        let changed = false;

        if (this._selection == this._droppedPin) {
            const position = coords[coords.length-1];

            changed = Math.abs(this!._droppedPin!.dist - distance) > 0.01;

            if (changed) {
                const formattedPosition = CoordinatesUtils.decimalToDegreesAndMinutes(position);
                const name = `${formattedPosition.lat} / ${formattedPosition.lon}`

                this._selection = this._droppedPin = {
                    value: name,
                    label: name,
                    longitude: position[0],
                    latitude: position[1],
                    dist: distance,
                    time: -1,
                    terminals: [],
                    coords,
                };
            }
        } else if (this._selection) {
            changed = Math.abs(this._selection.dist - distance) > 0.01;

            if (changed) {
                this._selection.coords = coords;
                this._selection.dist = distance;
            }
        }

        if (changed) {
            this._handleSpeedChange();
        }
    }

    private _handleKnownTerminalsUpdate(): void {
        this._nearestPortList = [];

        for (let [port, terminals] of TerminalsModel!.terminalsByCity.entries()) {
            const {longitude, latitude} = terminals.reduce((p, c) => {
                p.longitude += c.longitude/terminals.length;
                p.latitude += c.latitude/terminals.length;
                return p;
            }, {longitude: 0.0, latitude: 0.0});
            this._nearestPortList.push({value: port, label: '', dist: 0.0, time: -1, longitude, latitude, terminals});
        }

    }

    private _handlePositionUpdate(): void {
        if (!this.imo) {
            this._latestPosition = undefined;
            return;
        };
        this._latestPosition = VesselsTrackingModel?.filteredVesselPositions.get(this.imo);
    }

    private _handleSpeedChange(): void {
        if (!this._speedValue) return;

        const start = this._etd || Date.now();
        this._nearestPorts.forEach((p) => {
            p.time = start + (p.dist/this._speedValue!)*60*60*1000;
            p.etaUTC = this._formatDate(p.time);
            p.etaLT = '--';
        });
        this._manualAdditions.forEach((p) => {
            p.time = start + (p.dist/this._speedValue!)*60*60*1000;
            p.etaUTC = this._formatDate(p.time);
            p.etaLT = '--';
        })
        if (this._droppedPin && this._droppedPin.longitude && this._droppedPin.latitude) {
            this._droppedPin.time = start + (this._droppedPin.dist/this._speedValue)*60*60*1000;
            this._droppedPin.etaUTC = this._formatDate(this._droppedPin.time);
        }

        this._debouncedConvertToLocaltime([this._nearestPorts, this._manualAdditions]);
    }

    private _handleRangeChange(e: CustomEvent): void {
        e.stopPropagation();

        this._speedValue = e.detail.value;
    }

    private _handleInputChange(e: Event): void {
        e.stopPropagation();

        const { value } = e.target as HTMLInputElement

        this._speedValue = !value ? 1 : parseFloat(value)
    }

    private _handleETDChange(e: Event): void {
        const target = e.target as VoyTimeinput;
        this._etd = target.value;
    }

    private _manualAddPort(e: CustomEvent): void {
        e.stopPropagation();

        const { value } = e.detail;
        const selectedPort = this._nearestPortList.find((p) => p.value === value);

        if (selectedPort) {
            if (this._manualAdditions.length == 0) {
                this._manualAdditions.push(selectedPort);
            } else {
                this._manualAdditions[0] = selectedPort;
            }
            this.requestUpdate('_manualAdditions', null); // cheating and not passing the oldValue
            this.requestUpdate();
        }
        this._selection = selectedPort;
        this._searchField?.reset();
    }

    private _launchPortMemo(terminalCode: string): (e: Event) => void {
        return (e: Event) => {
            this.dispatchEvent(new CustomEvent('show-port-memo', {
                detail: {
                    terminalCode,
                },
                bubbles: true,
                composed: true,
            }));
        }
    }

    private async _convertToLocaltime(input: PortInfo[][]) {
        let conv: TimestampAndTerminal[] = [];
        for (let portlist of input) {
            for (let port of portlist) {
                conv.push({
                    timestamp: new Date(port.time).toISOString(),
                    terminalId: port.terminals[0].locationId,
                });
            }
        }

        let times = await TerminalsModel?.getTerminalLocaltimeBatch(conv);

        times?.reverse();
        for (let portlist of input) {
            for (let port of portlist) {
                const time = times?.pop() || 'Z';
                if (time?.substr(-1) === 'Z') {
                    // no timezone info known (or maybe it is UTC)
                    port.etaLT = this._formatDate(port.time);
                } else {
                    let offset = time?.substr(-5).split(':').map((s) => Number.parseInt(s));
                    let sign = time[time.length-6] === '+' ? 1 : -1;
                    port.etaLT = this._formatDate(port.time + sign*((offset[0]*60) + offset[1])*60*1000);
                }
            }
        }
        this.requestUpdate();
    }

    private _formatDate(ts: number): string {
        const monthNameMap = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const date = new Date(ts);
        const moreThanHalfAYear = (ts - Date.now()) > (182*24*60*60*1000); // roughly half a year

        return moreThanHalfAYear 
            ? '> 6 months'
            : `${date.getUTCDate()} ${monthNameMap[date.getUTCMonth()]} ${date.getUTCHours().toString().padStart(2, '0')}:${date.getUTCMinutes().toString().padStart(2, '0')}`;
    }

    private _onPortClick(port: PortInfo): void {
        this._selection = port;
    }

    private _renderPort(label: string, port: PortInfo): TemplateResult {
        const zone = port.terminals.find(el => el.olsonTimezone !== undefined)?.olsonTimezone;

        const etaLT = port.etaUTC && zone ? FormatDateToLocalTime(port.etaUTC, zone) : (port.etaLT || '');

        return html`
        <div class="port" ?selected=${this._selection == port} @click=${() => this._onPortClick(port)}>
            <header>
                <h2>${label}</h2>
                ${port.dist < 0 ? '' :
                html`
                    <div class="distance-and-dot-menu-container">
                        <div class="distance">
                            <div class="distance-wrapper">
                            <span class="distance-text">${port.dist >= 0 ? port.dist.toFixed(0) : ''} NM</span>
                                <span class="distance-tooltip">
                                    ${SvgIcons.lightQuestionCircle}
                                    <voyage-tooltip-proxy text="All initial distances are <br/> great circle distance - not <br/> exact route." html-content .positionPriority=${["top"]}></voyage-tooltip-proxy>
                                </span>
                            </div>
                        </div>
                        <div class="dot-menu" @click=${(e: MouseEvent) => this._renderPortMemosPopup(<MouseEvent>e, port)}> ${SvgIcons.ellipsisH} </div>
                    </div>
                `}
            </header>
            <p>
            ${port.dist < 0 ? html`&nbsp;` :
            html`
                <span class="eta">ETA:</span> ${etaLT || port.etaUTC + " UTC"}`}
            </p>
        </div>`;
    }

    private _renderPortMemosPopup(e: MouseEvent, port: PortInfo) {
        const xClick = (<HTMLElement>e.target).getClientRects()[0].right + 30;
        const yClick = (<HTMLElement>e.target).getClientRects()[0].top;
        const element = html`
            <style>
                .modal-content-container {
                    padding: 16px;
                }
                .modal-content-container button {
                    margin: 6px 0px;
                    padding: 0px;
                    background: none;
                    border: none;
                    outline: none;
                    border: none;
                    cursor: pointer;
                    display: flex;
                    gap: 5px;
                    align-items: center;
                    background-color: var(--neutrals-grey-10, white);
                    color: var(--voyage-blue-dark, blue);
                    fill: currentColor;
                    font-family: MaerskText;
                    font-size: 14px;
                }
            </style>
            <div class="modal-content-container">
                <span>Port Memos:</span>
                ${repeat(port.terminals, (terminal, index) => html`
                    <button @click=${(e: Event) => {this._launchPortMemo(terminal.locationId)(e); modal.close()}}>${SvgIcons.customFile}${terminal.locationId}</button>
                `)}
            </div>
        `

        const modal = VoyModalManager.createModal({
            modalPos: {x: xClick, y: yClick},
            content: element
        });
    }

    private _renderNearestPorts(): TemplateResult[] {
        const portFragments: TemplateResult[] = [];

        let portList = this._nearestPorts.slice();

        for (let port of this._nearestPorts) {
            portFragments.push(this._renderPort(`${port.terminals[0].city} (${port.value})`, port));
        }
        for (let port of this._manualAdditions) {
            portFragments.push(this._renderPort(`${port.terminals[0].city} (${port.value})`, port));
        }
        if (this._droppedPin) {
            portFragments.push(this._renderPort(`${this._droppedPin.label}`, this._droppedPin));
        }

        return portFragments;
    }

    private _renderSearchItems(port: PortInfo): TemplateResult {
        return html`<voy-fleet-sub-view-nearest-port-search-result left=${port.label} right=${port.dist.toFixed(0) + ' NM'}> </voy-fleet-sub-view-nearest-port-search-result>`
    }

    private _goBack() {
        NearestPortModel?.setPortHighLight({ports: []});
        VoyMapModel?.setZoomLevel(3);
        UiStateModel!.setValue(UiStateModelParam.FISubView, FiSubView.VesselDetails);
    }

    render() {
        const isPremium:boolean = this._vessel?.vesselVersion === VesselVoyageVersion.Premium;

        return html`
            <section>
                <header>
                    <button @click=${this._goBack} data-test="back-button">${SvgIcons.customChevronLeft}</button>
                    <div>
                        <h1><span class="premium-icon" ?hidden=${!isPremium}>${SvgIcons.customPremiumVessel}</span>${this._vessel?.vesselName || "N/A"} <br> Divert to</h1>
                    </div>
                </header>
            </section>
            <section class="calculator">
                <div>
                    <label for="speed-value">Speed</label>
                    <span class="speed-input">
                        <input id="speed-value" step="0.1" min="1" max="25" @input=${this._handleInputChange} .value=${this._speedValue ? this._speedValue : 1} />
                        <span>kt</span>
                    </span>
                </div>
                <voy-input-range min="1" max="25" .value=${this._speedValue ? this._speedValue : 1} @input=${this._handleRangeChange}></voy-input-range>
            </section>
            <section class="search">
                <voy-input-type-a-head placeholder="Search for port" .options=${this._nearestPortSearchResults} .renderItem=${this._renderSearchItems} @change=${this._manualAddPort}> </voy-input-type-a-head>
            </section>
            <div class="scrollhost">
                ${this._renderNearestPorts()}
            </div>
        `;
    }
}
